// deployment_class: customer_env_allowed
/**
 * LandingFactConstellation — the FACT CONSTELLATION SCHEMA view.
 *
 * Facts (the measures the agency keeps) sit on an inner ring; the CONFORMED
 * dimensions they are keyed by sit on an outer ring, with a spoke for every
 * fact→dimension key. The insight the view exists to deliver is the SHARING:
 * hover or select a dimension and every fact keyed by it lights up — that is what
 * makes a board question about one measure answerable against another without a
 * reconciliation. Hover or select a fact and its own dimensions light instead.
 *
 * Every measure value is read off the lineage tree at render time — this view never
 * re-states a figure (see landingSchemaViews' SSOT note).
 *
 * Shares the constellation's camera (zoom/pan), its detail popover and its legend,
 * so switching views is a change of question, not a change of instrument.
 */
import { useMemo, useState } from 'react';
import {
  SV_STAGE_H,
  SV_STAGE_W,
  layoutFactConstellation,
  type PlacedDimension,
  type PlacedFact,
} from './landingSchemaViews';
import { type NodeIndex } from './landingDrillData';
import { LandingDetailPopover, type DetailAnchor, type DetailPayload } from './LandingDetailPopover';

type CSSVars = React.CSSProperties & Record<string, string | number>;

export function LandingFactConstellation({ index }: { index: NodeIndex }): JSX.Element {
  const { facts, dims, links } = useMemo(() => layoutFactConstellation(index), [index]);

  /** The mark the viewer is pointing at (hover or focus) — drives the lit set. */
  const [hover, setHover] = useState<string | null>(null);
  /** The mark the viewer clicked — opens the detail popover and pins the lit set. */
  const [detail, setDetail] = useState<{ payload: DetailPayload; anchor: DetailAnchor } | null>(
    null,
  );
  const [pinned, setPinned] = useState<string | null>(null);

  const active = hover ?? pinned;

  // What lights up: a fact lights its own dimensions; a dimension lights every fact
  // keyed by it. That asymmetry is the schema being read in both directions.
  const lit = useMemo(() => {
    if (!active) return null;
    const fact = facts.find((f) => f.id === active);
    if (fact) return new Set<string>([fact.id, ...fact.dims]);
    const dim = dims.find((d) => d.id === active);
    if (dim) {
      return new Set<string>([dim.id, ...facts.filter((f) => f.dims.includes(dim.id)).map((f) => f.id)]);
    }
    return null;
  }, [active, facts, dims]);

  const isLit = (id: string): boolean => !lit || lit.has(id);
  const isDim = (id: string): boolean => !!lit && !lit.has(id);

  const openFact = (f: PlacedFact, el: HTMLElement): void => {
    const r = el.getBoundingClientRect();
    setPinned(f.id);
    setDetail({
      payload: {
        kindLabel: 'Fact',
        label: f.label,
        value: f.value,
        detail: `Grain: ${f.grain}. ${f.detail}`,
        status: f.status,
      },
      anchor: { top: r.top, left: r.left, width: r.width, height: r.height },
    });
  };

  const openDim = (d: PlacedDimension, el: HTMLElement): void => {
    const r = el.getBoundingClientRect();
    const users = facts.filter((f) => f.dims.includes(d.id)).map((f) => f.label);
    setPinned(d.id);
    setDetail({
      payload: {
        kindLabel: 'Conformed dimension',
        label: d.label,
        value: `${d.factCount} facts`,
        detail: `Grain: ${d.grain}. Shared by ${users.join(', ')} — so a question asked of any one of them can be answered against the others.`,
        status: d.factCount > 1 ? 'done' : 'neutral',
      },
      anchor: { top: r.top, left: r.left, width: r.width, height: r.height },
    });
  };

  const closeDetail = (): void => {
    setDetail(null);
    setPinned(null);
  };

  return (
    <>
      <svg
        className="kh-sv-links"
        viewBox={`0 0 ${SV_STAGE_W} ${SV_STAGE_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {links.map((l) => {
          const on = !lit || (lit.has(l.factId) && lit.has(l.dimId));
          return (
            <line
              key={l.id}
              className={`kh-sv-link${on ? ' is-on' : ' is-off'}`}
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
            />
          );
        })}
      </svg>

      {facts.map((f) => (
        <button
          type="button"
          key={f.id}
          className={`kh-sv-fact st-${f.status}${isLit(f.id) ? ' is-lit' : ''}${isDim(f.id) ? ' is-dim' : ''}`}
          style={{ left: `${f.x}px`, top: `${f.y}px` } as CSSVars}
          onMouseEnter={() => setHover(f.id)}
          onMouseLeave={() => setHover(null)}
          onFocus={() => setHover(f.id)}
          onBlur={() => setHover(null)}
          onClick={(e) => openFact(f, e.currentTarget)}
          aria-label={`Fact: ${f.label}${f.value ? `, ${f.value}` : ''}, keyed by ${f.dims.length} dimensions`}
        >
          <span className="kh-sv-fact-kind" aria-hidden="true">
            Fact
          </span>
          <span className="kh-sv-fact-label" aria-hidden="true">
            {f.label}
          </span>
          {f.value && (
            <span className="kh-sv-fact-value" aria-hidden="true">
              {f.value}
            </span>
          )}
        </button>
      ))}

      {dims.map((d) => (
        <button
          type="button"
          key={d.id}
          className={`kh-sv-dim${isLit(d.id) ? ' is-lit' : ''}${isDim(d.id) ? ' is-dim' : ''}${d.factCount > 1 ? ' is-conformed' : ''}`}
          style={{ left: `${d.x}px`, top: `${d.y}px` } as CSSVars}
          onMouseEnter={() => setHover(d.id)}
          onMouseLeave={() => setHover(null)}
          onFocus={() => setHover(d.id)}
          onBlur={() => setHover(null)}
          onClick={(e) => openDim(d, e.currentTarget)}
          aria-label={`Dimension: ${d.label}, shared by ${d.factCount} facts`}
        >
          <span className="kh-sv-dim-label" aria-hidden="true">
            {d.label}
          </span>
          <span className="kh-sv-dim-count" aria-hidden="true">
            {d.factCount}
          </span>
        </button>
      ))}

      {detail && (
        <LandingDetailPopover
          payload={detail.payload}
          anchor={detail.anchor}
          onClose={closeDetail}
        />
      )}
    </>
  );
}
